import { useState } from "react";
import { Copy } from "lucide-react";
import Modal from "./Modal";
import bookingService from "../api/services/bookingService";
import { useToast } from "../hooks/useToast";

const unwrap = (response) => response?.data || response || {};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// A single "I've sent the money" click checks a few times before telling the
// user to wait — bank transfers can take a little while to clear even after
// they've actually sent it.
const VERIFY_ATTEMPTS = 3;
const VERIFY_DELAY_MS = 2000;

/**
 * Custom bank-transfer screen. `account` is the one-time dedicated account
 * Paystack generated for this booking (via the Charge API's Pay-with-Transfer
 * channel) — passed down from PaymentMethodModal after it calls
 * bookingService.initBankTransfer. "I've sent the money" re-checks the real
 * payment status with our backend instead of trusting the click itself.
 */
function BankTransferModal({
  isOpen,
  onClose,
  onChangeMethod,
  onPaymentSuccess,
  totalPrice,
  account,
  bookingId,
  reference,
}) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [copied, setCopied] = useState(false);
  const toast = useToast();

  const handleCopy = async () => {
    if (!account?.accountNumber) return;
    try {
      await navigator.clipboard.writeText(account.accountNumber);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Couldn't copy to clipboard.");
    }
  };

  const handleConfirm = async () => {
    if (!reference) {
      toast.error("Missing transfer reference. Please go back and try again.");
      return;
    }

    setIsProcessing(true);
    try {
      for (let attempt = 0; attempt < VERIFY_ATTEMPTS; attempt += 1) {
        const verifyResponse = await bookingService.verifyPaystackPayment(
          reference
        );
        const { booking } = unwrap(verifyResponse);
        if (booking && booking.status !== "failed_payment") {
          if (onPaymentSuccess) onPaymentSuccess({ bookingId, booking });
          return;
        }
        if (attempt < VERIFY_ATTEMPTS - 1) {
          await sleep(VERIFY_DELAY_MS);
        }
      }
      toast.error(
        "We haven't received your transfer yet. Give it a moment and try again — we'll also confirm automatically once it clears."
      );
    } catch (err) {
      toast.apiError(err, "Unable to verify transfer. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const expiresAtLabel = account?.expiresAt
    ? new Date(account.expiresAt).toLocaleTimeString("en-NG", {
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} panelClassName="max-w-lg">
      <div className="space-y-6">
        <div className="space-y-2 pr-10">
          <h3 className="text-lg font-bold text-[#570033]">
            Pay with Bank Transfer
          </h3>
          <p className="text-sm text-slate-500">
            To complete your booking, kindly pay{" "}
            <span className="font-semibold text-[#570033]">{totalPrice}</span> to
            the account details provided below.
          </p>
        </div>

        <div className="rounded-2xl border border-slate-200 px-4 py-5 text-center">
          <p className="text-sm text-slate-500">Amount to pay:</p>
          <p className="mt-1 text-xl font-bold text-[#570033]">
            Price:&nbsp; {totalPrice}
          </p>
        </div>

        <div className="rounded-2xl border border-slate-200 px-5 py-5">
          <p className="text-sm font-semibold text-slate-500">Account Details</p>
          <div className="my-4 h-px bg-slate-200" />
          <div className="flex items-center gap-3">
            <span className="text-2xl font-bold tracking-wide text-slate-800">
              {account?.accountNumber || "—"}
            </span>
            <button
              type="button"
              onClick={handleCopy}
              className="flex items-center gap-1 text-sm text-slate-400 transition hover:text-[#570033]"
              aria-label="Copy account number"
            >
              <Copy className="h-5 w-5" />
              {copied ? (
                <span className="font-medium italic text-[#570033]">Copied!</span>
              ) : null}
            </button>
          </div>
          <p className="mt-4 text-base text-slate-700">
            <span className="font-bold text-slate-900">Bank:</span>{" "}
            {account?.bankName || "—"}
          </p>
          <p className="mt-2 text-base text-slate-700">
            <span className="font-bold text-slate-900">Name:</span>{" "}
            {account?.accountName || "—"}
          </p>
          {account?.narration ? (
            <p className="mt-2 text-base text-slate-700">
              <span className="font-bold text-slate-900">Narration:</span>{" "}
              {account.narration}
            </p>
          ) : null}
          {expiresAtLabel ? (
            <p className="mt-3 text-xs font-medium text-[#7A4C00]">
              This account expires around {expiresAtLabel}. Please transfer
              before then.
            </p>
          ) : null}
        </div>

        <div className="space-y-3">
          <button
            type="button"
            onClick={handleConfirm}
            className="w-full rounded-xl bg-[#570033] py-3 text-sm font-semibold text-white transition hover:bg-[#450029] disabled:cursor-not-allowed disabled:opacity-70"
            disabled={isProcessing || !account?.accountNumber}
          >
            {isProcessing ? "Checking..." : "I've sent the money"}
          </button>
          <button
            type="button"
            onClick={onChangeMethod}
            className="w-full rounded-xl border border-[#570033] py-3 text-sm font-semibold text-[#570033] transition hover:bg-[#F8EEF4]"
          >
            Change payment method
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default BankTransferModal;
